import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../utils/api';

const DEFAULT_CONFIG = {
  minimum_transaction: 500,
  maximum_transaction: 10000,
  payout_threshold: 100,
  platform_fee_percent: 2,
  currency: 'ZAR',
  currency_symbol: 'R',
  payment_methods: ['CARD', 'OZOW'],
  // Mirrors backend TRADESAFE_PROCESSING_FEE_PERCENT (backend/routes/transactions.py) —
  // the real per-method TradeSafe processing cost. Fallback only; /platform/settings
  // is the source of truth once loaded.
  payment_method_processing_fee_percent: { EFT: 0, CARD: 2.5, OZOW: 2.5, INSTANT_PAYOUT: 0 },
  // Authoritative, provider-CONFIRMED rate per method for THIS TrustTrade
  // TradeSafe account — null until TradeSafe confirms a real figure (see
  // backend TRADESAFE_PROCESSING_FEE_PERCENT_CONFIRMED). The Card/Ozow fee
  // comparison UI (PaymentMethodFeeCompare) reads ONLY this field, never
  // payment_method_processing_fee_percent above, which is an estimate only.
  payment_method_processing_fee_confirmed: { CARD: null, OZOW: null },
  // TradeSafe's own PUBLISHED integration-rate documentation (NOT an
  // account-confirmed rate — see backend TRADESAFE_PROCESSING_RATE_DOCUMENTED),
  // EXCLUDING VAT. Used by PaymentMethodFeeCompare only when
  // payment_method_processing_fee_confirmed above is still null for a
  // method; still pending cent-exact verification against a real
  // hosted-checkout total (the R500 Card test that charged R530.41 has not
  // yet been checked against this formula to the cent).
  payment_method_processing_rate_documented: { CARD: 3.0, OZOW: 1.5 },
  south_africa_vat_percent: 15,
  // Fail-closed fallbacks until /platform/settings loads: no method may show
  // a quantified estimate and the conditional fixed charge is unknown. See
  // backend TRADESAFE_DOCUMENTED_ESTIMATE_RECONCILED and
  // TRADESAFE_QUICK_SETTLEMENT_FIXED_FEE_RANDS.
  payment_method_documented_estimate_reconciled: { CARD: false, OZOW: false },
  provider_quick_settlement_fee: null,
  payout_release_times: ['10:00', '15:00'],
  payout_cutoff_times: ['09:00', '14:00'],
  payout_clearing_disclaimer: 'Bank clearing may take up to 2 business days depending on payment runs, weekends, and bank processing.',
  // New milestone deals stay hidden until GET /platform/settings says otherwise.
  milestones_enabled: false,
};

export const PlatformConfigContext = createContext({
  config: DEFAULT_CONFIG,
  loading: true,
  error: null,
});

export const MINIMUM_PLATFORM_FEE = 5;

function roundToCents(value) {
  return Math.round((value + (Number.EPSILON * Math.abs(value))) * 100) / 100;
}

export function calculateConfiguredPlatformFee(itemPrice, platformFeePercent) {
  const price = Number(itemPrice) || 0;
  const percent = Number(platformFeePercent) || 0;
  return Math.max(roundToCents(price * (percent / 100)), MINIMUM_PLATFORM_FEE);
}

export function resolveTransactionPlatformFee(transaction, platformFeePercent) {
  const storedFee = transaction?.platform_fee ?? transaction?.trusttrade_fee;
  return storedFee != null
    ? Number(storedFee)
    : calculateConfiguredPlatformFee(transaction?.item_price, platformFeePercent);
}

export function resolveBuyerPlatformFee(transaction, platformFeePercent) {
  if (transaction?.buyer_fee != null) return Number(transaction.buyer_fee);

  const platformFee = resolveTransactionPlatformFee(transaction, platformFeePercent);
  const allocation = String(
    transaction?.fee_allocation ?? transaction?.fee_paid_by ?? 'BUYER'
  ).toUpperCase();
  if (['SELLER', 'SELLER_AGENT'].includes(allocation)) return 0;
  if (['SPLIT', 'SPLIT_AGENT', 'BUYER_SELLER', 'BUYER_SELLER_AGENT'].includes(allocation)) {
    return roundToCents(platformFee / 2);
  }
  return platformFee;
}

function resolveBuyerTradesafeProcessingFee(transaction) {
  if (transaction?.tradesafe_processing_fee == null) return 0;

  const processingFee = Number(transaction.tradesafe_processing_fee) || 0;
  const allocation = String(
    transaction?.fee_allocation ?? transaction?.fee_paid_by ?? 'BUYER'
  ).toUpperCase();
  if (['SELLER', 'SELLER_AGENT'].includes(allocation)) return 0;
  if (['SPLIT', 'SPLIT_AGENT', 'BUYER_SELLER', 'BUYER_SELLER_AGENT'].includes(allocation)) {
    return roundToCents(processingFee / 2);
  }
  return processingFee;
}

export function resolveTransactionTotal(transaction, platformFeePercent) {
  if (transaction?.total != null) return Number(transaction.total);
  const total = (Number(transaction?.item_price) || 0)
    + (Number(transaction?.courier_fee) || 0)
    + resolveBuyerPlatformFee(transaction, platformFeePercent)
    + resolveBuyerTradesafeProcessingFee(transaction);
  return roundToCents(total);
}

export function PlatformConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    api.get('/platform/settings')
      .then((response) => {
        if (!active) return;
        setConfig((prev) => ({ ...prev, ...response.data }));
        setError(null);
      })
      .catch((err) => {
        if (!active) return;
        setError(err);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(() => ({ config, loading, error }), [config, loading, error]);

  return (
    <PlatformConfigContext.Provider value={value}>
      {children}
    </PlatformConfigContext.Provider>
  );
}

export function usePlatformConfig() {
  return useContext(PlatformConfigContext);
}

export { DEFAULT_CONFIG as DEFAULT_PLATFORM_CONFIG };
